<template>
    <ul @scroll="handleScroll" ref="list">
        <li v-for="(option) in lazyOptions" :key="option[idField]"
            class="preset-element"
            :class="{action: option.action, 'no-border': !showLeavesBorder, 'small': small}"
            @click="option.action && option.action()">
            <div class="font-8 wrap header" v-if="option.isHeader">
                {{option[displayField]}}
            </div>
            <div class="font-7 wrap select-all" v-else-if="option.isSelectAll">
                <input :id="`option${option[idField]}`"
                       type="checkbox"
                       :checked="isAllSelected"
                       :indeterminate.prop="!isAllSelected && selectedItems.length > 0"
                       @input="handleCheckAll(option, $event.target.checked)"
                >
                <label :for="`option${option[idField]}`"
                       class="truncate font-7 select-all-field">
                    {{option[displayField]}}
                </label>
            </div>
            <div class="font-7 wrap" v-else>
                <input :id="`option${option[idField]}`"
                       type="checkbox"
                       v-model="selection[option[idField]]"
                       @input="handleCheck(option, $event.target.checked)"
                >
                <slot :option="option" name="preOption"></slot>
                <label :for="`option${option[idField]}`"
                       :class="{'selected': isSelected(option[idField])}">
                    <Tooltip class="truncate option-tooltip-wrap"
                             :class="{'font-5': !small, 'font-7': small}"
                             :preventOverflow="false"
                             :text="option[displayField]">
                        {{option[displayField]}}
                    </Tooltip>
                </label>
                <slot :option="option" name="postOption"></slot>
            </div>
            <div class="description" v-if="option.description" v-once>
                <slot :option="option" name="description"></slot>
            </div>
        </li>
    </ul>
</template>

<script>
import Tooltip from 'components/Tooltip/Tooltip.vue';
import { difference } from 'lodash';

export default {
    name: 'MultipleSelectionLeavesDropdown',
    components: { Tooltip },
    props: {
        idField: String,
        options: Array,
        selectedItems: Array,
        displayField: String,
        markSelected: Boolean,
        small: Boolean,
        showLeavesBorder: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            selection: this.parseSelection(),
            currentMax: 30
        };
    },
    computed: {
        lazyOptions() {
            return this.options.slice(0, this.currentMax);
        },
        isAllSelected() {
            const options = this.options.filter((option) => !option.isSelectAll);
            return !difference(options, this.selectedItems).length;
        }
    },
    watch: {
        selectedItems() {
            this.selection = this.parseSelection();
        },
        options() {
            this.currentMax = this.options.length;
        }
    },
    methods: {
        parseSelection() {
            const selection = {};
            this.selectedItems.forEach((item) => {
                selection[item[this.idField]] = true;
            });
            return selection;
        },
        handleCheckAll(option, value) {
            this.$emit('dropdownSelectedAll', { isCheck: value, isAllSelected: this.isAllSelected });
        },
        handleCheck(option, value) {
            this.$emit('dropdownSelectedOption', { option, isCheck: value });
        },
        handleScroll() {
            const threshold = 100;
            const bulkScroll = 10;
            const visibleHeight = this.$refs.list.offsetHeight;

            const scrollableHeight = this.$refs.list.scrollHeight;
            const hiddenContentHeight = scrollableHeight - visibleHeight;

            if (hiddenContentHeight - this.$refs.list.scrollTop <= threshold) {
                // update index with next bulk
                this.currentMax += bulkScroll;

                // make sure last index is not bigger than the list length
                this.currentMax = this.options.length > this.currentMax ? this.currentMax : this.options.length;
            }
        },
        isSelected(key) {
            if (this.selection[key] && this.markSelected) {
                return true;
            }
            return '';
        }
    }
};
</script>

<style scoped lang="scss">
    @import './dropdownStyleGuide';
    @import "~styles/checkbox";

    $paddingLeftElements: 18px;

    .select-all {
        .select-all-field input[type='checkbox'] + label, label{
            @include font(7);
            &.selected {
                @include font(7);
            }
        }
    }

    .preset-element {
        overflow: hidden;
        padding-left: $paddingLeftElements;
        padding-right: $paddingLeftElements;
        white-space: nowrap;
        cursor: pointer;
        color: $dark-300;
        border-bottom: $border;

        &:last-child {
            border-bottom: none;
            margin-bottom: 4px;
        }

        &:hover {
            background-color: $light-100;
        }

        input[type='checkbox'] + label, label{
            cursor: pointer;
            line-height: 40px;
            margin-bottom: 0;
            margin-left: 0;
            max-width: calc(100% - 5px); // calc(max dropdown width - icons/checkboxes width);
            width: 100%;
            padding-right: 65px;
            vertical-align: middle;
            display: inline-block;
            position: relative;

            .option-tooltip-wrap {
                width: 100%;
                vertical-align: middle;
            }
        }
        input[type='checkbox'] {
            margin-bottom: 0;
            margin-left: 0;
            vertical-align: middle;
        }
    }

    .wrap {
        position: relative;
    }

    .truncate {
        &.selected {
            font-weight: 700;
        }
    }

    .header {
        color: $light-800;
        cursor: default;
        position: relative;
        line-height: 40px;
    }

    .no-border {
        border: none;
    }

    .small {
        input[type='checkbox'] + label, label{
            line-height: 35px;
            @include font(7);
        }
    }

</style>
